import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Iter 120 — Generic 401 handling. When a live session goes stale (cookie
// expired, or an admin purged all sessions), every subsequent call 401s and
// pages used to fail silently. Bounce to /login with a generic notice and
// keep the current URL as `next` so the user lands back where they were.
// Auth/OAuth endpoints are excluded: those own their error copy (e.g. a
// wrong password on the login form must not trigger a redirect loop).
const AUTH_PATHS = ["/auth/", "/oauth/"];

// Iter 175 — CSRF. The session cookie is SameSite=None (the app runs inside
// the Emergent preview iframe), so cookie-authenticated writes must carry a
// token only same-origin JS can read. Fetched lazily before the first write
// and refreshed once if the server says it's stale.
const SAFE_METHODS = ["get", "head", "options"];
// Writes that create or destroy the session itself — no token exists yet (or
// it belongs to the session being replaced), so skip and reset.
const CSRF_BOOTSTRAP = ["/auth/login", "/auth/google-session", "/auth/mfa/verify",
                        "/auth/logout", "/oauth/"];
let csrfToken = null;
let csrfInflight = null;

async function fetchCsrfToken() {
  if (!csrfInflight) {
    csrfInflight = axios
      .get(`${API}/auth/csrf`, { withCredentials: true })
      .then((r) => { csrfToken = r.data?.csrf_token || null; return csrfToken; })
      .catch(() => null)
      .finally(() => { csrfInflight = null; });
  }
  return csrfInflight;
}

export function clearCsrfToken() { csrfToken = null; }

api.interceptors.request.use(async (config) => {
  const method = (config.method || "get").toLowerCase();
  if (SAFE_METHODS.includes(method)) return config;
  const url = config.url || "";
  if (CSRF_BOOTSTRAP.some((p) => url.includes(p))) { csrfToken = null; return config; }
  if (!csrfToken) await fetchCsrfToken();
  if (csrfToken) config.headers["X-CSRF-Token"] = csrfToken;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const cfg = err.config || {};
    const isCsrf = err.response?.status === 403
      && /csrf/i.test(String(err.response?.data?.detail || ""));
    if (isCsrf && !cfg.__csrfRetried) {
      cfg.__csrfRetried = true;
      csrfToken = null;
      await fetchCsrfToken();
      if (csrfToken) {
        cfg.headers = { ...(cfg.headers || {}), "X-CSRF-Token": csrfToken };
        return api.request(cfg);
      }
    }
    return Promise.reject(err);
  },
);

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;
    const url = err.config?.url || "";
    if (status === 401
        && !AUTH_PATHS.some((p) => url.includes(p))
        && !window.location.pathname.startsWith("/login")) {
      const here = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.location.assign(`/login?reason=expired&next=${encodeURIComponent(here)}`);
    }
    // Iter 275 — a 403 that nobody catches surfaces in the error log as a
    // minified "Request failed with status code 403" with no clue which call
    // was refused. Name the endpoint and the server's reason in the message so
    // the log row is actionable.
    if (status === 403) {
      const method = (err.config?.method || "get").toUpperCase();
      const reason = err.response?.data?.detail;
      err.message = `Request failed with status code 403 — ${method} ${url}`
        + (typeof reason === "string" && reason.length < 160 ? ` — ${reason}` : "");
    }
    if (status === 429) {
      const now = Date.now();
      if (now - lastThrottleToast > 10000) {
        lastThrottleToast = now;
        toast.error(formatApiErrorDetail(err.response?.data?.detail), { id: "rate-limited" });
      }
    }
    return Promise.reject(err);
  },
);

let lastThrottleToast = 0;

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") {
    // A proxy/CDN (Cloudflare, nginx) returned an HTML error page, not our JSON.
    if (/<html|<!doctype|cloudflare|<body/i.test(detail) || detail.length > 300)
      return "Something went wrong. Please try again.";
    return detail;
  }
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
